/**
 * Applies the admin-controlled DYNAMIC brand theme (from GET /api/ui/config) as inline CSS
 * variables on <html>, overriding the static accent palette. This lets an outlet's Settings →
 * Theme choice (any hex + light/dark) drive BOTH the customer web app and the admin panel. Empty
 * values fall back to the design default, so an untouched tenant keeps the built-in look.
 */

function clamp(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function parseHex(hex?: string): [number, number, number] | null {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function toHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('');
}

/** Mix a colour toward another by `amt` (0..1). */
function mix(rgb: [number, number, number], toward: [number, number, number], amt: number): [number, number, number] {
  return [rgb[0] + (toward[0] - rgb[0]) * amt, rgb[1] + (toward[1] - rgb[1]) * amt, rgb[2] + (toward[2] - rgb[2]) * amt];
}

const BLACK: [number, number, number] = [0, 0, 0];
const WHITE: [number, number, number] = [255, 255, 255];

/** Perceived luminance (0..255) — decides black vs white foreground on the accent. */
function luminance([r, g, b]: [number, number, number]): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export interface BrandTheme {
  accent?: string;
  secondary?: string;
  mode?: string; // "light" | "dark"
}

/**
 * Write the accent family (+ optional deep/rail colour) onto document.documentElement as inline
 * custom properties. Pass `mode` to also flip data-theme. Call again with the same values to update.
 */
export function applyBrandTheme(theme: BrandTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  if (theme.mode === 'light' || theme.mode === 'dark') {
    root.setAttribute('data-theme', theme.mode);
  }
  const dark = root.getAttribute('data-theme') === 'dark';

  const accent = parseHex(theme.accent);
  if (accent) {
    const hover = mix(accent, BLACK, 0.12);
    const light = dark ? `rgba(${clamp(accent[0])}, ${clamp(accent[1])}, ${clamp(accent[2])}, 0.16)` : toHex(mix(accent, WHITE, 0.86));
    const fg = luminance(accent) > 165 ? '#1b1b1b' : '#ffffff';
    root.style.setProperty('--color-primary', toHex(accent));
    root.style.setProperty('--color-primary-hover', toHex(hover));
    root.style.setProperty('--color-primary-light', light);
    root.style.setProperty('--color-primary-fg', fg);
    root.style.setProperty('--gradient-primary', `linear-gradient(150deg, ${toHex(mix(accent, WHITE, 0.14))}, ${toHex(mix(accent, BLACK, 0.16))})`);
  }

  const secondary = parseHex(theme.secondary);
  if (secondary) {
    // The deep brand colour drives the side rail / dark chrome (garden forest green by default).
    root.style.setProperty('--color-bg-rail', toHex(secondary));
  }
}
